import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import { useUser, useClerk } from "@clerk/clerk-react";
import {
  Accordion,
  AccordionHeader,
  AccordionBody,
  Button,
  Select,
  Option,
  IconButton,
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
  Avatar,
  Typography
} from "@material-tailwind/react";
import {
  PencilIcon,
  StarIcon,
  TagIcon,
  FolderIcon,
  PowerIcon,
  ArrowPathIcon,
  TrashIcon,
  PlusCircleIcon,
  ChevronDownIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";
import { collection, addDoc, deleteDoc, doc, getDocs, getDoc, query, where, setDoc, onSnapshot, serverTimestamp } from "firebase/firestore";
import CreateTagGroupModal from "./CreateTagGroupModal";
import MyProfileModal from "./MyProfileModal";

export default function Sidebar({
  userInfo = {},
  threads = [],
  updateThread = () => {},
  allTags = [],
  favoriteTags = [],
  setShowCreateModal // ✅ Added this line
}) {
  const { user } = useUser();
  // Debug logging for props
  // console.log('Sidebar props:', { allTags, favoriteTags });

  // Helper to format bytes in a user-friendly way
  function formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }
  const [showGroups, setShowGroups] = useState(true);
  const [groups, setGroups] = useState([]);
  const [myTags, setMyTags] = useState([]);
  const { signOut } = useClerk();
  useEffect(() => {
    if (!user) return;
    try {
      const tagsRef = collection(db, "users", user.id, "tags");
      const unsubscribe = onSnapshot(tagsRef, (snapshot) => {
        const loaded = snapshot.docs.map((doc) => doc.data().name);
        setMyTags(loaded);
      });
      return () => unsubscribe();
    } catch (e) {
      console.warn("Error listening to user tags:", e);
      setMyTags([]);
    }
  }, [user?.id]);
  const [editingGroup, setEditingGroup] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [openTags, setOpenTags] = useState(true);
  const [openFavorites, setOpenFavorites] = useState(true);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const thread = threads[0] || {};
  // Debug logging for thread state
  // console.log('Sidebar thread:', thread);
  const {
    selectedTags = [],
    sidebarMatchMode = "all",
    selectedGroupId = null,
    selectedGroupName = "",
    filterSource = "tags",
  } = thread;

  useEffect(() => {
    if (!user) return;
    const fetchGroups = async () => {
      try {
        const userDocRef = doc(db, 'users', user.id);
        const userDocSnap = await getDoc(userDocRef);
        if (!userDocSnap.exists()) {
          console.warn('⚠️ No Firestore user found for Clerk user:', user.id);
          return;
        }
        const internalUserId = userDocSnap.id;

        const q = query(collection(db, 'tagGroups'), where('userId', '==', internalUserId));
        const querySnapshot = await getDocs(q);
        const fetchedGroups = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setGroups(fetchedGroups);
      } catch (e) {
        console.warn("Error fetching tag groups:", e);
      }
    };
    fetchGroups();
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    const loadFavorites = async () => {
      try {
        const favRef = doc(db, 'favorites', user.id);
        const favSnap = await getDoc(favRef);
        if (favSnap.exists()) {
          updateThread(0, {
            ...thread,
            allTags,
            favoriteTags: favSnap.data().tags || [],
            selectedTags,
            sidebarMatchMode,
            selectedGroupId,
            selectedGroupName,
            filterSource,
          });
        } else {
          updateThread(0, {
            ...thread,
            allTags,
            favoriteTags: [],
            selectedTags,
            sidebarMatchMode,
            selectedGroupId,
            selectedGroupName,
            filterSource,
          });
        }
      } catch (e) {
        console.warn("Unable to load favorites:", e);
        updateThread(0, {
          ...thread,
          allTags,
          favoriteTags: [],
          selectedTags,
          sidebarMatchMode,
          selectedGroupId,
          selectedGroupName,
          filterSource,
        });
      }
    };

    loadFavorites();
  }, [user?.id]);

  if (!user) {
    return null;
  }

  const handleDeleteGroup = async (groupId) => {
    await deleteDoc(doc(db, 'tagGroups', groupId));
    setGroups(groups.filter((g) => g.id !== groupId));
  };

  return (
    <div className="w-full h-full overflow-y-auto p-4 bg-white border-r flex flex-col justify-between">
      {/* Top Section */}
      <div>
        <Menu>
          <MenuHandler>
            <div className="flex items-center gap-3 cursor-pointer hover:bg-blue-gray-50 p-2 rounded-lg transition mb-2">
              <Avatar
                src={user.imageUrl}
                alt="avatar"
                size="sm"
                className="border border-blue-gray-100"
              />
              <div className="text-left">
                <Typography variant="small" className="text-gray-900 font-medium">
                  {userInfo.name}
                </Typography>
                <Typography variant="small" className="text-blue-gray-500 font-normal">
                  {userInfo.thoughtCount} thoughts • {formatBytes(userInfo.totalSize)}
                </Typography>
              </div>
            </div>
          </MenuHandler>
        <MenuList className="py-1 px-2">
          <MenuItem onClick={() => setShowProfileModal(true)}>
            My Profile
          </MenuItem>
          <MenuItem onClick={() => signOut()} className="text-red-500">
            Log Out
          </MenuItem>
        </MenuList>
        </Menu>
        <Button
          fullWidth
          className="flex items-center justify-center gap-2 mb-2 bg-[rgba(16,131,254,1)] hover:bg-[rgba(16,131,254,0.85)] text-white rounded-full"
          onClick={() => setShowCreateModal(true)}
        >
          <PencilIcon className="h-5 w-5" />
          New Thought
        </Button>
      </div>

      {/* Viewing Area */}
      {((filterSource === "group" && selectedGroupName !== undefined) || (filterSource === "tags" && selectedTags.length > 0)) && (
        <div className="mt-4 p-3 border border-blue-gray-100 rounded-md bg-blue-gray-50">
          <div className="flex justify-between items-center mb-2">
            <p className="text-xs text-blue-gray-700 font-medium">
              {filterSource === "group" && selectedGroupName !== undefined && (
                <span className="ml-1 font-semibold text-blue-gray-900 transition-transform duration-300 transform animate-fade-in">
                  {selectedGroupName}
                </span>
              )}
            </p>
            {filterSource === "tags" && selectedTags.length > 0 && (
              <span className="text-xs font-semibold text-blue-gray-700">{selectedTags.length}</span>
            )}
          </div>
          {filterSource === "tags" && (
            <div className="mt-2 transition-transform duration-300 transform animate-fade-in">
              {selectedTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-block bg-blue-gray-100 text-blue-gray-800 text-xs px-2 py-1 rounded-full mr-1 mb-1"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
          <div className="mt-3 flex items-center justify-between">
            <div className="w-1/2">
              <Select
                size="sm"
                label="Match Mode"
                value={sidebarMatchMode}
                onChange={(val) =>
                  updateThread(0, {
                    ...thread,
                    allTags,
                    favoriteTags,
                    selectedTags,
                    sidebarMatchMode: val,
                    selectedGroupId,
                    selectedGroupName,
                    filterSource,
                  })
                }
              >
                <Option value="all">All</Option>
                <Option value="any">Any</Option>
              </Select>
            </div>
            <IconButton
              variant="text"
              color="blue-gray"
              onClick={() => {
                updateThread(0, {
                  ...thread,
                  allTags,
                  favoriteTags,
                  selectedTags: [],
                  sidebarMatchMode: "all",
                  selectedGroupId: null,
                  selectedGroupName: "",
                  filterSource: "tags",
                });
              }}
              title="Clear Filters"
            >
              <ArrowPathIcon className="h-4 w-4" />
            </IconButton>
          </div>
        </div>
      )}

      {/* Accordions */}
      <div className="flex-1 mt-4">
        <Accordion open={openTags} icon={<ChevronDownIcon className={`h-4 w-4 transition-transform ${openTags ? "rotate-180" : ""}`} />}>
          <AccordionHeader onClick={() => setOpenTags(!openTags)}>
            <div className="flex items-center gap-2">
              <TagIcon className="h-5 w-5" />
              Tags
            </div>
          </AccordionHeader>
          <AccordionBody className="pt-2">
            <ul className="pl-4">
              {myTags.map((tag) => (
                <li
                  key={tag}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("text/plain", tag)}
                  className={`cursor-grab select-none py-1 mb-1 rounded-lg transition-colors ${
                    selectedTags.includes(tag)
                      ? "bg-blue-100 text-blue-800 font-semibold"
                      : "hover:bg-blue-gray-50 text-gray-700"
                  }`}
                  onClick={() => {
                    const newSelectedTags = selectedTags.includes(tag)
                      ? selectedTags.filter((t) => t !== tag)
                      : [...selectedTags, tag];
                    updateThread(0, {
                      ...thread,
                      allTags: myTags,
                      favoriteTags,
                      selectedTags: newSelectedTags,
                      sidebarMatchMode,
                      selectedGroupId: null,
                      selectedGroupName: "",
                      filterSource: "tags",
                    });
                  }}
                >
                  <div className="px-2 flex items-center justify-between w-full">
                    <span>{tag}</span>
                    <Button
                      variant="text"
                      size="sm"
                      className="ml-2 p-0 hover:bg-yellow-50 transition"
                      onClick={async (e) => {
                        e.stopPropagation();
                        // Ensure the tag exists in the user's private tag list
                        if (!myTags.includes(tag)) {
                          await setDoc(doc(db, "users", user.id, "tags", tag), {
                            name: tag,
                            createdAt: serverTimestamp(),
                          });
                        }
                        // Now proceed to toggle favorites
                        const favRef = doc(db, 'favorites', user.id);
                        const favSnap = await getDoc(favRef);
                        if (!favSnap.exists()) {
                          await setDoc(favRef, { tags: [tag] });
                        } else {
                          const existing = favSnap.data().tags || [];
                          let updatedFavs;
                          if (existing.includes(tag)) {
                            updatedFavs = existing.filter((t) => t !== tag);
                          } else {
                            updatedFavs = [...existing, tag];
                          }
                          await setDoc(favRef, { tags: updatedFavs }, { merge: true });
                        }
                        // Update thread's favoriteTags to reflect the change
                        updateThread(0, {
                          ...thread,
                          allTags: myTags,
                          favoriteTags: favoriteTags.includes(tag)
                            ? favoriteTags.filter((t) => t !== tag)
                            : [...favoriteTags, tag],
                          selectedTags,
                          sidebarMatchMode,
                          selectedGroupId,
                          selectedGroupName,
                          filterSource,
                        });
                      }}
                      aria-label={favoriteTags.includes(tag) ? "Unfavorite" : "Favorite"}
                    >
                      <StarIcon
                        className={`h-4 w-4 ${
                          favoriteTags.includes(tag) ? "text-yellow-400" : "text-gray-400"
                        }`}
                      />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </AccordionBody>
        </Accordion>

        <Accordion open={openFavorites} icon={<ChevronDownIcon className={`h-4 w-4 transition-transform ${openFavorites ? "rotate-180" : ""}`} />}>
          <AccordionHeader onClick={() => setOpenFavorites(!openFavorites)}>
            <div className="flex items-center gap-2">
              <StarIcon className="h-5 w-5" />
              Favorites
            </div>
          </AccordionHeader>
          <AccordionBody className="pt-2">
            <ul className="pl-4">
              {favoriteTags.map((tag) => (
                <li
                  key={tag}
                  className={`cursor-pointer flex justify-between items-center rounded-lg py-1 px-2 mb-1 transition-colors ${
                    selectedTags.includes(tag)
                      ? "bg-yellow-50 text-blue-700 font-semibold"
                      : "hover:bg-yellow-50 text-blue-600"
                  }`}
                  onClick={() => {
                    const newSelectedTags = selectedTags.includes(tag)
                      ? selectedTags.filter((t) => t !== tag)
                      : [...selectedTags, tag];
                    updateThread(0, {
                      ...thread,
                      allTags,
                      favoriteTags,
                      selectedTags: newSelectedTags,
                      sidebarMatchMode,
                      selectedGroupId: null,
                      selectedGroupName: "",
                      filterSource: "tags",
                    });
                  }}
                >
                  <span>{tag}</span>
                  <StarIcon className="h-4 w-4 text-yellow-400" />
                </li>
              ))}
            </ul>
          </AccordionBody>
        </Accordion>

        <Accordion open={showGroups} icon={<ChevronDownIcon className={`h-4 w-4 transition-transform ${showGroups ? "rotate-180" : ""}`} />}>
          <AccordionHeader onClick={() => setShowGroups(!showGroups)}>
            <div className="flex items-center gap-2">
              <FolderIcon className="h-5 w-5" />
              Groups
            </div>
          </AccordionHeader>
          {showGroups && (
            <AccordionBody className="pt-2">
              {groups.map((group) => (
                <div key={group.id} className="pl-6 py-1">
                  {editingGroup?.id === group.id ? (
                    <CreateTagGroupModal
                      isEdit
                      initialName={editingGroup.name}
                      initialTags={editingGroup.tags}
                      initialMatchAll={editingGroup.matchAll}
                      onSave={async (updatedGroup) => {
                        await setDoc(doc(db, 'tagGroups', editingGroup.id), updatedGroup, { merge: true });
                        setGroups(groups.map((g) => (g.id === editingGroup.id ? { ...g, ...updatedGroup } : g)));
                        setEditingGroup(null);
                      }}
                      onClose={() => setEditingGroup(null)}
                    />
                  ) : (
                  <div className="flex justify-between items-center">
                    <div
                      role="button"
                      tabIndex={0}
                      className={`text-sm cursor-pointer text-left ${
                        selectedGroupId?.toString() === group.id.toString()
                          ? 'text-red-600'
                          : 'text-gray-800 hover:text-blue-600'
                      }`}
                      onClick={() => {
                        updateThread(0, {
                          ...thread,
                          allTags,
                          favoriteTags,
                          selectedTags: group.tags || [],
                          sidebarMatchMode: group.matchAll ? 'all' : 'any',
                          selectedGroupId: group.id.toString(),
                          selectedGroupName: group.name || "",
                          filterSource: "group",
                        });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          updateThread(0, {
                            ...thread,
                            allTags,
                            favoriteTags,
                            selectedTags: group.tags || [],
                            sidebarMatchMode: group.matchAll ? 'all' : 'any',
                            selectedGroupId: group.id.toString(),
                            selectedGroupName: group.name || "",
                            filterSource: "group",
                          });
                        }
                      }}
                    >
                      {group.name}
                    </div>
                    <Menu placement="left-start">
                      <MenuHandler>
                        <IconButton
                          variant="text"
                          size="sm"
                          className="hover:bg-blue-gray-50"
                          title="Group Actions"
                        >
                          <EllipsisVerticalIcon className="w-5 h-5 text-gray-600" />
                        </IconButton>
                      </MenuHandler>
                      <MenuList>
                        <MenuItem
                          onClick={() => {
                            const alreadyExists = threads.some(
                              (t) =>
                                t.filterSource === "group" &&
                                t.selectedGroupName === group.name
                            );
                            if (!alreadyExists) {
                              const newThread = {
                                selectedTags: group.tags || [],
                                selectedGroupName: group.name,
                                filterMatchMode: group.matchAll ? 'all' : 'any',
                                filterSource: "group",
                                allTags,
                                favoriteTags
                              };
                              const newThreads = [...threads, newThread];
                              updateThread(newThreads.length - 1, newThread);
                            }
                          }}
                        >
                          View Group Thread
                        </MenuItem>
                        <MenuItem onClick={() => setEditingGroup(group)}>
                          Edit Group
                        </MenuItem>
                        <MenuItem onClick={() => handleDeleteGroup(group.id)} className="text-red-500">
                          Delete Group
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </div>
                  )}
                </div>
              ))}
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outlined"
                  size="sm"
                  onClick={() => setShowModal(true)}
                  className="flex items-center gap-2"
                >
                  <PlusCircleIcon className="w-4 h-4" />
                  New Group
                </Button>
              </div>
              {showModal && (
                <CreateTagGroupModal
                  onClose={() => setShowModal(false)}
                  onCreateGroup={async ({ name, tags, matchAll }) => {
                    // TODO: Replace user.id with internal Firestore userId after resolving and passing it down
                    const newGroup = {
                      userId: user.id, // This will need to be replaced with the internalUserId once we pass it down
                      name,
                      tags,
                      matchAll,
                    };
                    const docRef = await addDoc(collection(db, 'tagGroups'), newGroup);
                    setGroups([...groups, { ...newGroup, id: docRef.id }]);
                  }}
                />
              )}
            </AccordionBody>
          )}
        </Accordion>
      </div>

      {/* Bottom Sign Out removed, now handled in avatar menu */}
      {showProfileModal && (
        <MyProfileModal
          userInfo={userInfo}
          onClose={() => setShowProfileModal(false)}
        />
      )}
    </div>
  );
}